import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fireDB } from '../../../../Backend/Firebase/FirebaseConfigData';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';

interface Subtopic {
  title: string;
  author?: string;
  blogLink?: string;
  driveLink?: string;
  metadata?: string;
  dataType?: string;
  fileType?: string;
  createdAt?: unknown;
}

const SubtopicForm = () => {
  const { id, subId } = useParams<{ id: string; subId?: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<Subtopic>({
    title: '',
    author: '',
    blogLink: '',
    driveLink: '',
    metadata: '',
    dataType: 'notes',
    fileType: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  // If editing, fetch subtopic data
  useEffect(() => {
    const fetchSubtopic = async () => {
      if (!id || !subId) return;
      setIsEditing(true);
      const docRef = doc(fireDB, 'libraryEntries', id, 'subtopics', subId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setForm(docSnap.data() as Subtopic);
      }
    };
    fetchSubtopic();
  }, [id, subId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      if (isEditing && subId) {
        await updateDoc(doc(fireDB, 'libraryEntries', id, 'subtopics', subId), form);
        alert('Subtopic updated successfully!');
      } else {
        await addDoc(collection(fireDB, 'libraryEntries', id, 'subtopics'), {
          ...form,
          createdAt: serverTimestamp(),
        });
        alert('Subtopic added successfully!');
      }
      navigate(`/elibrary/update/${id}`);
    } catch (error) {
      console.error('Error saving subtopic:', error);
      alert('Error saving subtopic.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-md rounded-lg p-6 max-w-2xl mx-auto"
      >
        <h2 className="text-2xl font-semibold mb-4 text-center">
          {isEditing ? '✏️ Edit Subtopic' : '➕ Add New Subtopic'}
        </h2>

        <label className="block mb-2 font-medium">Title</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
          required
        />

        <label className="block mb-2 font-medium">Author</label>
        <input
          type="text"
          value={form.author}
          onChange={(e) => setForm({ ...form, author: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Drive Link</label>
        <input
          type="url"
          value={form.driveLink}
          onChange={(e) => setForm({ ...form, driveLink: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Blog Link</label>
        <input
          type="url"
          value={form.blogLink}
          onChange={(e) => setForm({ ...form, blogLink: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">File Type</label>
        <input
          type="text"
          value={form.fileType}
          onChange={(e) => setForm({ ...form, fileType: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Data Type</label>
        <select
          value={form.dataType}
          onChange={(e) => setForm({ ...form, dataType: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        >
          <option value="notes">Notes</option>
          <option value="pyqs">PYQs</option>
          <option value="blog">Blog</option>
          <option value="tutorial">Tutorial</option>
        </select>

        <label className="block mb-2 font-medium">Metadata</label>
        <textarea
          value={form.metadata}
          onChange={(e) => setForm({ ...form, metadata: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <button
          type="submit"
          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition"
        >
          {isEditing ? 'Update Subtopic' : 'Create Subtopic'}
        </button>
      </form>
    </div>
  );
};

export default SubtopicForm;
